import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useCallback, useId, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { prefectures } from "~/constants/PrefectureConst";
import type {
	CompanyStoreSetting,
	CompanyStoreSettingEdit
} from "~/models/CompanyStoreSetting";
import { validationSchemaCompanyStoreSetting } from "~/models/CompanyStoreSetting";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/CompanyStoreSetting";
import { onInvalidScroll } from "~/utils/FormUtil";
import { generatePath } from "~/utils/generatePath";

const NOTE_ROWS = 6;
const NOTE_MAX_ROWS = 10;

type Props = {
	companyStoreSetting: CompanyStoreSetting;
};

export function CompanyStoreSettingForm({ companyStoreSetting }: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const handleOpenConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);
	const handleCloseConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(false);
	}, []);
	const {
		formState: { errors },
		handleSubmit,
		register
	} = useForm<CompanyStoreSettingEdit>({
		defaultValues: companyStoreSetting,
		mode: "onBlur",
		resolver: yupResolver(validationSchemaCompanyStoreSetting)
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/itandi_account_sessions/current_account"),
		async (_key, { arg }: { arg: CompanyStoreSettingEdit }) => update(arg)
	);

	const handleAccept = useCallback(
		async (submitValue: CompanyStoreSettingEdit) => {
			const response = await trigger(submitValue);
			if (isSuccess(response)) {
				notify({ content: "保存しました。", variant: "success" });
				handleCloseConfirmModal();
				return;
			}
			notify({ content: "保存に失敗しました。", variant: "error" });
		},
		[handleCloseConfirmModal, notify, trigger]
	);

	return (
		<>
			<Form
				id={formId}
				onSubmit={handleSubmit(handleOpenConfirmModal, onInvalidScroll)}
			>
				<FormGroup maxW="32rem" variant="vertical">
					<FormControl chip="必須" isInvalid={!!errors.name} label="店舗名">
						<Input {...register("name")} />
						<FormControlText error={errors.name?.message} />
					</FormControl>
					<FormControl
						chip="必須"
						isInvalid={!!errors.zipcode}
						label="郵便番号"
					>
						<Input {...register("zipcode")} />
						<FormControlText error={errors.zipcode?.message} />
					</FormControl>
					<FormControl chip="必須" isInvalid={!!errors.state} label="都道府県">
						<InputSelect w="100%" {...register("state")}>
							<option value="">選択してください</option>
							{prefectures.map((prefecture) => (
								<option key={prefecture.id} value={prefecture.name}>
									{prefecture.name}
								</option>
							))}
						</InputSelect>
						<FormControlText error={errors.state?.message} />
					</FormControl>
					<FormControl
						chip="必須"
						isInvalid={!!errors.city}
						label="市区町村・番地"
					>
						<Input {...register("city")} />
						<FormControlText error={errors.city?.message} />
					</FormControl>
					<FormControl label="建物・部屋番号">
						<Input {...register("otherAddress")} />
					</FormControl>
					<FormControl
						chip="必須"
						isInvalid={!!errors.phoneNumber}
						label="店舗の代表電話番号"
					>
						<Input {...register("phoneNumber")} />
						<FormControlText error={errors.phoneNumber?.message} />
					</FormControl>
					<FormControl label="オーナー見積書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="オーナー用見積書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("billingEstimationNoteForOwner")}
						/>
					</FormControl>
					<FormControl label="入居者見積書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="入居者用見積書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("billingEstimationNoteForRentee")}
						/>
					</FormControl>
					<FormControl label="オーナー請求書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="オーナー用請求書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("billingNoteForOwner")}
						/>
					</FormControl>
					<FormControl label="入居者請求書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="入居者用請求書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("billingNoteForRentee")}
						/>
					</FormControl>
					<FormControl label="オーナー精算書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="オーナー用精算書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("settlementNoteForOwner")}
						/>
					</FormControl>
					<FormControl label="入居者精算書用備考">
						<InputText
							maxRows={NOTE_MAX_ROWS}
							placeholder="入居者用精算書エクセルファイルに記載したい備考を入力してください"
							resize="autoRows"
							rows={NOTE_ROWS}
							{...register("settlementNoteForRentee")}
						/>
					</FormControl>
				</FormGroup>
			</Form>
			<Buttonv2
				form={formId}
				marginTop={designTokens["spacing/m"]}
				type="submit"
				variant="primary"
			>
				保存
			</Buttonv2>
			{isConfirmModalOpen ? (
				<Modalv2
					isOpen
					onClose={isMutating ? () => undefined : handleCloseConfirmModal}
					size="s"
				>
					<ModalHeader title="設定の保存" />
					<ModalBody>
						<Paragraph>設定を保存します。</Paragraph>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isMutating}
								onClick={handleCloseConfirmModal}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								loading={isMutating}
								onClick={handleSubmit(handleAccept)}
								variant="primary"
							>
								保存
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
